/**
 * Audit Logs Page
 * 
 * View and filter system-wide audit logs for all admin actions.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Avatar,
} from '@mui/material';
import {
  ArrowBack,
  Timeline,
  Search,
  FilterList,
  Refresh,
  Download,
  AdminPanelSettings,
  Login,
  Logout,
  Add,
  Edit,
  Delete,
  ContentCopy,
  ToggleOn,
  PersonAdd,
  Settings,
  Schedule,
} from '@mui/icons-material';

const AuditLogs = () => {
  const navigate = useNavigate();
  const { api, isAuthenticated, loading: authLoading } = useSuperAdmin();

  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [error, setError] = useState('');
  const [organizations, setOrganizations] = useState([]);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/superadmin/login');
      return;
    }
    fetchLogs();
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    applyFilters();
  }, [logs, searchQuery, actionFilter, dateFilter, sourceFilter, orgFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/superadmin/audit-logs/', {
        params: { limit: 500 }
      });
      setLogs(response.data.logs || []);
      setOrganizations(response.data.organizations || []);
    } catch (err) {
      setError('Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Source filter
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(log => log.source === sourceFilter);
    }

    // Organization filter
    if (orgFilter !== 'all') {
      filtered = filtered.filter(log =>
        log.organization_code === orgFilter || log.organization === orgFilter
      );
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log =>
        log.admin?.toLowerCase().includes(query) ||
        log.action?.toLowerCase().includes(query) ||
        log.target?.toLowerCase().includes(query) ||
        log.organization?.toLowerCase().includes(query) ||
        log.ip_address?.includes(query)
      );
    }

    // Action filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action === actionFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }

      filtered = filtered.filter(log => new Date(log.timestamp) >= filterDate);
    }

    // Sort by timestamp (newest first)
    filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    setFilteredLogs(filtered);
  };

  const getActionIcon = (action) => {
    const icons = {
      // SuperAdmin actions
      login: <Login fontSize="small" />,
      logout: <Logout fontSize="small" />,
      create_org: <Add fontSize="small" />,
      update_org: <Edit fontSize="small" />,
      delete_org: <Delete fontSize="small" />,
      clone_org: <ContentCopy fontSize="small" />,
      enable_feature: <ToggleOn fontSize="small" />,
      disable_feature: <ToggleOn fontSize="small" />,
      create_admin: <PersonAdd fontSize="small" />,
      update_settings: <Settings fontSize="small" />,
      // Permit actions
      created: <Add fontSize="small" />,
      updated: <Edit fontSize="small" />,
      assigned: <PersonAdd fontSize="small" />,
      activated: <ToggleOn fontSize="small" />,
      deactivated: <ToggleOn fontSize="small" />,
      cancelled: <Delete fontSize="small" />,
      renewed: <Settings fontSize="small" />,
      // Chalan actions
      paid: <Settings fontSize="small" />,
      fee_updated: <Edit fontSize="small" />,
      disputed: <Delete fontSize="small" />,
      resolved: <ToggleOn fontSize="small" />,
      deleted: <Delete fontSize="small" />,
    };
    return icons[action] || <Timeline fontSize="small" />;
  };

  const getActionColor = (action) => {
    const colors = {
      // SuperAdmin actions
      login: { bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
      logout: { bg: 'rgba(245,158,11,0.15)', text: '#f59e0b' },
      create_org: { bg: 'rgba(59,130,246,0.15)', text: '#3b82f6' },
      update_org: { bg: 'rgba(99,102,241,0.15)', text: '#6366f1' },
      delete_org: { bg: 'rgba(239,68,68,0.15)', text: '#ef4444' },
      clone_org: { bg: 'rgba(139,92,246,0.15)', text: '#8b5cf6' },
      enable_feature: { bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
      disable_feature: { bg: 'rgba(239,68,68,0.15)', text: '#ef4444' },
      create_admin: { bg: 'rgba(59,130,246,0.15)', text: '#3b82f6' },
      update_settings: { bg: 'rgba(99,102,241,0.15)', text: '#6366f1' },
      // Permit actions
      created: { bg: 'rgba(59,130,246,0.15)', text: '#3b82f6' },
      updated: { bg: 'rgba(99,102,241,0.15)', text: '#6366f1' },
      assigned: { bg: 'rgba(139,92,246,0.15)', text: '#8b5cf6' },
      activated: { bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
      deactivated: { bg: 'rgba(245,158,11,0.15)', text: '#f59e0b' },
      cancelled: { bg: 'rgba(239,68,68,0.15)', text: '#ef4444' },
      renewed: { bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
      // Chalan actions
      paid: { bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
      fee_updated: { bg: 'rgba(245,158,11,0.15)', text: '#f59e0b' },
      disputed: { bg: 'rgba(239,68,68,0.15)', text: '#ef4444' },
      resolved: { bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
      deleted: { bg: 'rgba(239,68,68,0.15)', text: '#ef4444' },
    };
    return colors[action] || { bg: 'rgba(107,114,128,0.15)', text: '#6b7280' };
  };

  const getSourceColor = (source) => {
    const colors = {
      superadmin: { bg: 'rgba(99,102,241,0.2)', text: '#818cf8' },
      permits: { bg: 'rgba(16,185,129,0.2)', text: '#10b981' },
      chalans: { bg: 'rgba(245,158,11,0.2)', text: '#f59e0b' },
    };
    return colors[source] || { bg: 'rgba(107,114,128,0.15)', text: '#6b7280' };
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const exportLogs = () => {
    const csv = [
      ['Timestamp', 'Source', 'Organization', 'User', 'Action', 'Target', 'IP Address'].join(','),
      ...filteredLogs.map(log => [
        formatTimestamp(log.timestamp),
        log.source || '-',
        log.organization || '-',
        log.admin,
        log.action,
        log.target || '-',
        log.ip_address || '-',
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      }}>
        <CircularProgress sx={{ color: '#818cf8' }} size={48} />
        <Typography sx={{ mt: 2, color: '#94a3b8' }}>Loading audit logs...</Typography>
      </Box>
    );
  }

  const uniqueActions = [...new Set(logs.map(log => log.action))];

  return (
    <Box sx={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    }}>
      {/* App Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'transparent',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 4 } }}>
          <IconButton
            onClick={() => navigate('/superadmin/dashboard')}
            sx={{ color: 'white', mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
          }}>
            <Timeline sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, color: 'white' }}>
            Audit Logs
          </Typography>

          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchLogs}
            sx={{
              mr: 2,
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={exportLogs}
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)' },
            }}
          >
            Export CSV
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ px: { xs: 2, sm: 4 }, py: 4, maxWidth: 1400, mx: 'auto' }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, bgcolor: '#450a0a', color: '#fecaca', border: '1px solid #dc2626' }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
            Audit Logs
          </Typography>
          <Typography sx={{ color: '#94a3b8' }}>
            Track all administrative actions across the system.
          </Typography>
        </Box>

        {/* Filters */}
        <Paper sx={{
          p: 3,
          mb: 3,
          bgcolor: '#1e293b',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 3,
        }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: '#94a3b8' }}>Action</InputLabel>
              <Select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                label="Action"
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                  '& .MuiSvgIcon-root': { color: '#94a3b8' },
                }}
              >
                <MenuItem value="all">All Actions</MenuItem>
                {uniqueActions.map(action => (
                  <MenuItem key={action} value={action}>
                    {action.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: '#94a3b8' }}>Time Period</InputLabel>
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                label="Time Period"
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                  '& .MuiSvgIcon-root': { color: '#94a3b8' },
                }}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">Last 7 Days</MenuItem>
                <MenuItem value="month">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Second row of filters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: '#94a3b8' }}>Source</InputLabel>
              <Select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                label="Source"
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                  '& .MuiSvgIcon-root': { color: '#94a3b8' },
                }}
              >
                <MenuItem value="all">All Sources</MenuItem>
                <MenuItem value="superadmin">SuperAdmin Portal</MenuItem>
                <MenuItem value="permits">Permits</MenuItem>
                <MenuItem value="chalans">Chalans</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel sx={{ color: '#94a3b8' }}>Organization</InputLabel>
              <Select
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value)}
                label="Organization"
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                  '& .MuiSvgIcon-root': { color: '#94a3b8' },
                }}
              >
                <MenuItem value="all">All Organizations</MenuItem>
                {organizations.map(org => (
                  <MenuItem key={org.code} value={org.code}>
                    {org.name} ({org.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${filteredLogs.length} entries`}
              size="small"
              sx={{ bgcolor: 'rgba(99,102,241,0.2)', color: '#818cf8' }}
            />
            {(searchQuery || actionFilter !== 'all' || dateFilter !== 'all' || sourceFilter !== 'all' || orgFilter !== 'all') && (
              <Button
                size="small"
                onClick={() => {
                  setSearchQuery('');
                  setActionFilter('all');
                  setDateFilter('all');
                  setSourceFilter('all');
                  setOrgFilter('all');
                }}
                sx={{ color: '#94a3b8' }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </Paper>

        {/* Logs Table */}
        <Paper sx={{
          bgcolor: '#1e293b',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 3,
          overflow: 'hidden',
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.03)' }}>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    Timestamp
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    Source
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    Organization
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    User
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    Action
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    Target
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLogs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((log, index) => {
                    const actionColor = getActionColor(log.action);
                    const sourceColor = getSourceColor(log.source);
                    return (
                      <TableRow
                        key={log.id || index}
                        sx={{
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                        }}
                      >
                        <TableCell sx={{ color: '#94a3b8', borderBottom: 'none' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule sx={{ fontSize: 16, color: '#64748b' }} />
                            {formatTimestamp(log.timestamp)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }}>
                          <Chip
                            label={log.source || 'system'}
                            size="small"
                            sx={{
                              bgcolor: sourceColor.bg,
                              color: sourceColor.text,
                              textTransform: 'capitalize',
                              fontSize: '0.7rem',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#94a3b8', borderBottom: 'none' }}>
                          {log.organization || '-'}
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#6366f1', fontSize: 12 }}>
                              {log.admin?.[0]?.toUpperCase()}
                            </Avatar>
                            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.875rem' }}>
                              {log.admin}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }}>
                          <Chip
                            icon={getActionIcon(log.action)}
                            label={(log.action_display || log.action).replace(/_/g, ' ')}
                            size="small"
                            sx={{
                              bgcolor: actionColor.bg,
                              color: actionColor.text,
                              '& .MuiChip-icon': { color: actionColor.text },
                              textTransform: 'capitalize',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#94a3b8', borderBottom: 'none' }}>
                          {log.target || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredLogs.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid rgba(255,255,255,0.1)',
              color: '#94a3b8',
              '& .MuiTablePagination-selectIcon': { color: '#94a3b8' },
              '& .MuiIconButton-root': { color: '#94a3b8' },
              '& .MuiIconButton-root.Mui-disabled': { color: '#475569' },
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default AuditLogs;
